import type { RetentionType } from 'api/shared/retentionPolicy';

export type RetentionUnit = 'exec' | 'min' | 'days' | 'months';

export interface RetentionOption {
  readonly value: RetentionType;
  readonly label: string;
  readonly unit: RetentionUnit;
  readonly placeholder: string;
}

export type RetentionPolicy = {
  status?: string;
  retention_type?: RetentionType | string;
  value?: number | '' | string;
};

export const RETENTION_TYPE_OPTIONS: readonly RetentionOption[] = [
  {
    value: 'executions_to_keep',
    label: 'Executions to keep',
    unit: 'exec',
    placeholder: 'e.g., 1000',
  },
  {
    value: 'minutes_to_keep',
    label: 'Minutes to keep',
    unit: 'min',
    placeholder: 'e.g., 10',
  },
  {
    value: 'days_to_keep',
    label: 'Days to keep',
    unit: 'days',
    placeholder: 'e.g., 30',
  },
  {
    value: 'months_to_keep',
    label: 'Months to keep',
    unit: 'months',
    placeholder: 'e.g., 6',
  },
];

const DEFAULT_RETENTION_OPTION = RETENTION_TYPE_OPTIONS.find((opt) => opt.value === 'days_to_keep') ?? RETENTION_TYPE_OPTIONS[0];

function getRetentionOption(retentionType?: string): RetentionOption {
  return RETENTION_TYPE_OPTIONS.find((opt) => opt.value === retentionType) ?? DEFAULT_RETENTION_OPTION;
}

export function retentionTypeUnit(retentionType?: string): RetentionUnit {
  return getRetentionOption(retentionType).unit;
}

export function retentionTypePlaceholder(retentionType?: string): string {
  return getRetentionOption(retentionType).placeholder;
}

function isPlainObject(v: any) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// BE format: { "0": {...}, "1": {...} }
// UI format: [{...}, {...}]
export function retentionPoliciesToArray(value: any): RetentionPolicy[] {
  if (Array.isArray(value)) return value as RetentionPolicy[];
  if (!isPlainObject(value)) return [];

  const keys = Object.keys(value)
    .filter((k) => String(Number(k)) === k)
    .sort((a, b) => Number(a) - Number(b));

  return keys.map((k) => value[k]) as RetentionPolicy[];
}

export function retentionPoliciesToBE(policies: RetentionPolicy[]): Record<string, RetentionPolicy> {
  const next: Record<string, RetentionPolicy> = {};
  (policies ?? []).forEach((p, idx) => {
    next[String(idx)] = p;
  });
  return next;
}

export function isRetentionPoliciesBE(value: any) {
  return isPlainObject(value);
}
